import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionService } from '@galaxy/core';
import { GetPartnerModelRequest, PartnerModelApiService } from '@galaxy/partner';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, merge, Observable, of, tap } from 'rxjs';
import { catchError, map, shareReplay, skipWhile, switchMap, take } from 'rxjs/operators';
import { PartnerSwitcherService } from '../../features/omni-search/partner-switcher/partner-switcher.service';
import { AuthApiService, PartnerPersonaWithPartnerId } from './auth.api.service';

export interface User {
  email: string;
  isSuperAdmin: boolean;
  partnerId?: string;
  userId?: string;
  created?: Date;
}

const CURRENT_PARTNER_KEY = 'vcc:currentPartner';
const PARTNER_QUERY_PARAM = '_pid';

@Injectable()
export class AuthService {
  private readonly partnerId$$ = new BehaviorSubject<string>('');
  public readonly partnerId$: Observable<string>;
  readonly currentUser$: Observable<User>;

  constructor(
    private authApiService: AuthApiService,
    private sessionService: SessionService,
    private router: Router,
    private route: ActivatedRoute,
    private partnerModelService: PartnerModelApiService,
    private partnerSwitcherService: PartnerSwitcherService,
  ) {
    const sessionId$ = this.sessionService.getSessionId();

    // Redirect to login if we have no sessionId
    sessionId$.pipe(take(1)).subscribe((sessionId) => {
      if (!sessionId) {
        this.redirectToLogin();
      }
    });

    this.currentUser$ = sessionId$.pipe(
      // TODO determine why sessionId is being set to null string
      skipWhile((sessionId) => sessionId === 'null'),
      switchMap((sessionId) => {
        return this.getSession(sessionId);
      }),
      filter((u) => !!u),
      map((partnerPersona) => {
        return this.buildUser(partnerPersona);
      }),
      shareReplay(1),
    );

    const queryParamPartnerId$ = this.route.queryParamMap.pipe(
      map((queryParamMap) => queryParamMap.get(PARTNER_QUERY_PARAM) ?? ''),
      map((partnerId) => partnerId.toUpperCase()),
      distinctUntilChanged(),
    );
    const initialPartnerId$ = combineLatest([this.currentUser$, queryParamPartnerId$]).pipe(
      switchMap(([user, queryParamPartnerId]) => {
        let partnerId$ = of(user.partnerId);

        // Superadmin users are allowed to switch PIDs and should start with the query parameter
        // or the local storage, which stores their previous PID.
        if (user.isSuperAdmin) {
          const defaultPartnerId = localStorage.getItem(CURRENT_PARTNER_KEY) ?? user.partnerId;
          partnerId$ = of(defaultPartnerId);
          if (queryParamPartnerId) {
            partnerId$ = this.isValidPartner$(queryParamPartnerId).pipe(
              map((isValid) => {
                if (isValid) {
                  return queryParamPartnerId;
                }
                return defaultPartnerId;
              }),
            );
          }
        }

        // Clear the query parameter PID so switching to a different PID works.
        if (queryParamPartnerId) {
          partnerId$ = partnerId$.pipe(
            tap(() => {
              this.router.navigate([], { queryParams: { [PARTNER_QUERY_PARAM]: null }, queryParamsHandling: 'merge' });
            }),
          );
        }
        return partnerId$;
      }),
    );

    this.partnerId$ = merge(initialPartnerId$, this.partnerId$$.asObservable()).pipe(
      filter((id) => !!id),
      distinctUntilChanged(),
      tap((partnerId) => {
        localStorage.setItem(CURRENT_PARTNER_KEY, partnerId);
        this.partnerSwitcherService.saveRecentPartner(partnerId);
      }),
    );
  }

  public get isSuperAdmin$(): Observable<boolean> {
    return this.currentUser$.pipe(map((u) => (u ? u.isSuperAdmin : false)));
  }

  public get isAuthenticated$(): Observable<boolean> {
    return this.sessionService.getSessionId().pipe(map((sessionId) => sessionId !== 'null' && !!sessionId));
  }

  public login(credentials: { email: string; password: string }): Observable<void> {
    return this.authApiService.login(credentials).pipe(
      map((sessionId) => {
        return this.sessionService.setSessionId(sessionId);
      }),
    );
  }

  public logout(): Observable<string> {
    return this.partnerId$.pipe(
      take(1),
      switchMap((partnerId) => {
        this.sessionService.clearSessionId();
        return this.authApiService.logout(partnerId);
      }),
    );
  }

  public setPartnerId(partnerId: string): Observable<SetPartnerResult> {
    return this.partnerId$.pipe(
      take(1),
      switchMap((currentPartnerId): Observable<SetPartnerResult> => {
        if (currentPartnerId === partnerId) return of('alreadySelected');
        return this.isValidPartner$(partnerId).pipe(
          tap((isValid) => {
            if (isValid) {
              this.partnerId$$.next(partnerId);
            }
          }),
          map((isValid) => (isValid ? 'success' : 'invalidPartner')),
        );
      }),
    );
  }

  private isValidPartner$(partnerId: string): Observable<boolean> {
    return this.partnerModelService.getPartnerModel(new GetPartnerModelRequest({ partnerId: partnerId })).pipe(
      map((resp) => Boolean(resp?.partner)),
      catchError(() => of(false)),
    );
  }

  private getSession(sessionId: string): Observable<PartnerPersonaWithPartnerId> {
    if (!sessionId) {
      return of(null);
    }
    if (this.atExternalContext()) {
      return of(null);
    }

    return this.authApiService.getPartnerPersonaBySession(sessionId).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 403 || err.status === 401) {
          this.redirectToPermissionDenied();
          return of(null);
        }
        this.sessionService.clearSessionId();
        this.redirectToLogin();
        return of(null);
      }),
    );
  }

  private buildUser(partner: PartnerPersonaWithPartnerId): User {
    if (!partner) {
      return null;
    }
    const user = {} as User;
    user.partnerId = partner.partnerId;
    user.userId = partner.userId;
    user.email = partner.email;
    user.isSuperAdmin = partner.isSuperAdmin;
    user.created = partner.created;
    return user;
  }

  // Return true if at a route "protocol://domain/docs"
  private atExternalContext(): boolean {
    const url = window.location.href;
    const origin = window.location.origin;
    return url.indexOf(origin + '/docs') >= 0;
  }

  private redirectToLogin(): void {
    let alreadyAtLogin: boolean;
    let atRegister: boolean;
    // Angular router does not report my path properly
    const url = window.location.href;
    if (url.endsWith) {
      alreadyAtLogin = url.includes('/login');
      atRegister = url.includes('/register');
    } else {
      alreadyAtLogin = url.indexOf('/login') >= 0;
      atRegister = url.indexOf('/login') >= 0;
    }
    const atExternalContext = this.atExternalContext();

    if (!alreadyAtLogin && !atExternalContext && !atRegister) {
      //This is to ensure we pass only the route ex) /products to next url and not https://vendors.vendasta.com/products
      this.router.navigate(['/login'], { queryParamsHandling: 'preserve' });
    }
  }

  private redirectToPermissionDenied(): void {
    let alreadyAtLogin: boolean;
    let alreadyAtPermissionDenied: boolean;

    const url = window.location.href;
    if (url.endsWith) {
      alreadyAtLogin = url.endsWith('/login');
      alreadyAtPermissionDenied = url.endsWith('/permission-denied');
    } else {
      alreadyAtLogin = url.indexOf('/login') >= 0;
      alreadyAtPermissionDenied = url.indexOf('/permission-denied') >= 0;
    }
    const atExternalContext = this.atExternalContext();

    if (!alreadyAtLogin && !alreadyAtPermissionDenied && !atExternalContext) {
      this.router.navigate(['/permission-denied']);
    }
  }
}

type SetPartnerResult = 'success' | 'invalidPartner' | 'alreadySelected';
